import type { RecommendPageParams } from 'src/types/recommendation_items';
import * as XLSX from 'xlsx';

export type StagedActionType = 'swap' | 'addToCart' | 'punchout';

export interface StagedActionForExport {
  action: StagedActionType;
  item: {
    description: string;
    supplierName: string;
    itemNumber: string;
    manufacturerName: string;
    manufacturerNumber: string;
  };
}

export interface BlockDisplayForExport {
  description: string;
}

export interface ModifiedExportEntry {
  staged: StagedActionForExport;
  blockDisplay: BlockDisplayForExport | undefined;
  blockContext: RecommendPageParams | null;
}

/** Full modified list (panel): includes Alternative Supplier + Action */
export const MODIFIED_PANEL_COLUMNS = [
  'poLineItemNum',
  'originalProduct',
  'originalItemNum',
  'originalQty',
  'selectedAlternative',
  'alternativeSupplier',
  'alternativeItemNum',
  'alternativeDistributor',
  'alternativeManufacturer',
  'alternativeMfgNum',
  'action',
] as const;

export const MODIFIED_PANEL_COLUMN_MAPPING: Record<string, string> = {
  poLineItemNum: 'PO Line Item #',
  originalProduct: 'Original Product',
  originalItemNum: 'Original Item #',
  originalQty: 'Original Qty',
  selectedAlternative: 'Selected Alternative',
  alternativeSupplier: 'Alternative Supplier',
  alternativeItemNum: 'Alternative Item #',
  alternativeDistributor: 'Alternative Distributor',
  alternativeManufacturer: 'Alternative Manufacturer',
  alternativeMfgNum: 'Alternative Mfg #',
  action: 'Action',
};

/** Punchout supplier-level: no Alternative Supplier, no Action */
export const PUNCHOUT_SUPPLIER_COLUMNS = [
  'poLineItemNum',
  'originalProduct',
  'originalItemNum',
  'originalQty',
  'selectedAlternative',
  'alternativeItemNum',
  'alternativeDistributor',
  'alternativeManufacturer',
  'alternativeMfgNum',
] as const;

export const PUNCHOUT_SUPPLIER_COLUMN_MAPPING: Record<string, string> = {
  poLineItemNum: 'PO Line Item #',
  originalProduct: 'Original Product',
  originalItemNum: 'Original Item #',
  originalQty: 'Original Qty',
  selectedAlternative: 'Selected Alternative',
  alternativeItemNum: 'Alternative Item #',
  alternativeDistributor: 'Alternative Distributor',
  alternativeManufacturer: 'Alternative Manufacturer',
  alternativeMfgNum: 'Alternative Mfg #',
};

export const RECOMMENDATION_EXPORT_COLUMNS = [
  'poLineItemNum',
  'productName',
  'itemNum',
  'originalQty',
  'recommendationRank',
  'recommendedProductName',
  'recommendedItemNumber',
  'recommendedDistributor',
  'manufacturer',
  'mfgNumber',
  'allowedOperations',
  'preferredManufacturer',
] as const;

export const RECOMMENDATION_EXPORT_COLUMN_MAPPING: Record<string, string> = {
  poLineItemNum: 'PO Line Item #',
  productName: 'Original Product',
  itemNum: 'Original Item #',
  originalQty: 'Original Qty',
  recommendationRank: 'Recommendation Rank',
  recommendedProductName: 'Alternative Product Name',
  recommendedItemNumber: 'Alternative Item #',
  recommendedDistributor: 'Alternative Distributor',
  manufacturer: 'Alternative Manufacturer',
  mfgNumber: 'Alternative Mfg #',
  allowedOperations: 'Allowed Operations',
  preferredManufacturer: 'Preferred Manufacturer',
};

function originalProductName(
  blockDisplay: BlockDisplayForExport | undefined,
  itemNum: string
): string {
  const fullDesc = blockDisplay?.description ?? '';
  if (itemNum && fullDesc.endsWith(' ' + itemNum)) {
    return fullDesc.slice(0, -(itemNum.length + 1)).trim();
  }
  return fullDesc;
}

function exportActionLabel(entry: ModifiedExportEntry): string {
  if (entry.staged.action === 'punchout') return 'Added to Punchout List';
  if (entry.staged.action === 'swap') return 'Item Swapped';
  if (entry.staged.action === 'addToCart') return 'Added to Cart';
  return '';
}

export function buildModifiedPanelExportRows(
  entries: ModifiedExportEntry[]
): Record<string, string>[] {
  return entries.map((e) => {
    const ctx = e.blockContext;
    const itemNum = ctx?.itemNumber ?? '';
    const alt = e.staged.item;
    return {
      poLineItemNum: ctx?.lineItemId != null ? String(ctx.lineItemId) : '',
      originalProduct: originalProductName(e.blockDisplay, itemNum),
      originalItemNum: itemNum,
      originalQty: ctx?.quantity != null ? String(ctx.quantity) : '',
      selectedAlternative: alt.description ?? '',
      alternativeSupplier: alt.supplierName ?? '',
      alternativeItemNum: alt.itemNumber ?? '',
      alternativeDistributor: alt.supplierName ?? '',
      alternativeManufacturer: alt.manufacturerName ?? '',
      alternativeMfgNum: alt.manufacturerNumber ?? '',
      action: exportActionLabel(e),
    };
  });
}

export function buildPunchoutSupplierExportRows(
  entries: ModifiedExportEntry[]
): Record<string, string>[] {
  return entries.map((e) => {
    const ctx = e.blockContext;
    const itemNum = ctx?.itemNumber ?? '';
    const alt = e.staged.item;
    return {
      poLineItemNum: ctx?.lineItemId != null ? String(ctx.lineItemId) : '',
      originalProduct: originalProductName(e.blockDisplay, itemNum),
      originalItemNum: itemNum,
      originalQty: ctx?.quantity != null ? String(ctx.quantity) : '',
      selectedAlternative: alt.description ?? '',
      alternativeItemNum: alt.itemNumber ?? '',
      alternativeDistributor: alt.supplierName ?? '',
      alternativeManufacturer: alt.manufacturerName ?? '',
      alternativeMfgNum: alt.manufacturerNumber ?? '',
    };
  });
}

export const RECOMMENDATION_DISCLAIMER =
  'Item may be sold in a different case pack or unit size. Quantities are not automatically adjusted.';

/** Local date YYYY-MM-DD for export filenames */
export function getRecommendationExportDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

export function sanitizeSupplierFileSegment(name: string): string {
  return (
    name
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '_')
      .trim()
      .slice(0, 80) || 'Supplier'
  );
}

// ---------------------------------------------------------------------------
// Recommendation Excel workbook builder (message row, merges, column widths)
// ---------------------------------------------------------------------------

const fitToColumn = (arrayOfArray: string[][]): { wch: number }[] =>
  arrayOfArray[0].map((_, i: number) => ({
    wch: Math.max(...arrayOfArray.map((a2) => (a2[i] ? a2[i].toString().length : 0))),
  }));

export interface ExportRecommendationsExcelProps {
  columns: string[];
  rows: Record<string, string | number>[];
  columnMapping: Record<string, string>;
  fileName: string;
  sheetName?: string;
  /** Merged across all columns on row 1 */
  message?: string;
  /** Empty row after message, before header (matches template row 2) */
  blankRowAfterMessage?: boolean;
  /** Vertically merge first N columns per group when group has multiple rows */
  mergeLeadingColumnCount?: number;
  /** Consecutive row counts per original line item / group */
  groupRowCounts: number[];
}

/**
 * Export recommendation data to Excel: optional disclaimer row, optional blank row,
 * header + data, and vertical merges on leading columns per group.
 */
export function exportRecommendationsExcel({
  columns,
  rows,
  columnMapping,
  fileName,
  sheetName = 'Sheet1',
  message,
  blankRowAfterMessage = false,
  mergeLeadingColumnCount = 4,
  groupRowCounts,
}: ExportRecommendationsExcelProps): void {
  const colCount = columns.length;
  const headerRow = columns.map((col) => columnMapping[col] || col);
  const rowArrays = rows.map((row) =>
    columns.map((col) => (row[col] !== undefined && row[col] !== null ? String(row[col]) : ''))
  );

  const data: string[][] = [];
  const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];

  if (message) {
    data.push([message, ...Array(colCount - 1).fill('')]);
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
  }
  if (blankRowAfterMessage) {
    data.push(Array(colCount).fill(''));
  }
  data.push(headerRow);
  data.push(...rowArrays);

  const firstDataRowIndex = data.length - rowArrays.length;
  let r = firstDataRowIndex;
  const mergeCols = Math.min(mergeLeadingColumnCount, colCount);
  for (const count of groupRowCounts) {
    if (count > 1 && mergeCols > 0) {
      for (let c = 0; c < mergeCols; c++) {
        merges.push({ s: { r, c }, e: { r: r + count - 1, c } });
      }
    }
    r += count;
  }

  const ws = XLSX.utils.aoa_to_sheet(data);
  if (merges.length > 0) {
    ws['!merges'] = merges;
  }
  // Base column widths on header + data only, so the message row doesn't stretch column A
  const tableStart = message ? (blankRowAfterMessage ? 2 : 1) : 0;
  ws['!cols'] = fitToColumn(data.slice(tableStart));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}
